import { useEffect, useRef, type KeyboardEvent } from "react";
import { Link, useLocation, useParams } from "wouter";
import { FileText, LayoutGrid } from "lucide-react";

const destinations = [
  { key: "boards", label: "Concept Boards", icon: FileText, suffix: "/boards" },
  { key: "types", label: "Board Types", icon: LayoutGrid, suffix: "/types" },
] as const;

export function ConceptBoardsWorkspaceNav() {
  const { brandId } = useParams<{ brandId: string }>();
  const [location] = useLocation();
  const navRef = useRef<HTMLElement>(null);
  const basePath = `/brands/${encodeURIComponent(brandId ?? "")}/template-studio`;
  const currentPath = location.split(/[?#]/, 1)[0].replace(/\/+$/, "") || "/";

  useEffect(() => {
    navRef.current?.querySelector<HTMLAnchorElement>('[aria-current="page"]')
      ?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [currentPath]);

  function handleKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;

    const links = navRef.current?.querySelectorAll<HTMLAnchorElement>("a[href]");
    if (!links?.length) return;

    const activeIndex = Array.from(links).indexOf(document.activeElement as HTMLAnchorElement);
    let nextIndex = activeIndex;
    if (event.key === "Home") nextIndex = 0;
    else if (event.key === "End") nextIndex = links.length - 1;
    else if (activeIndex >= 0) {
      nextIndex = (activeIndex + (event.key === "ArrowRight" ? 1 : -1) + links.length) % links.length;
    } else {
      nextIndex = 0;
    }

    event.preventDefault();
    links[nextIndex].focus();
    links[nextIndex].scrollIntoView({ block: "nearest", inline: "nearest" });
  }

  return (
    <nav
      ref={navRef}
      className="concept-boards-workspace-nav"
      aria-label="Concept boards workspace"
      onKeyDown={handleKeyDown}
    >
      <div className="concept-boards-workspace-nav__inner">
        <div className="concept-boards-workspace-nav__identity" aria-label="Template Studio">
          <span className="concept-boards-workspace-nav__mark" aria-hidden="true" />
          <span>Template Studio</span>
        </div>
        <div className="concept-boards-workspace-nav__items">
          {destinations.map(({ key, label, icon: Icon, suffix }) => {
            const href = `${basePath}${suffix}`;
            const active = currentPath === href || (key === "types" && currentPath === `${basePath}/library`);

            return (
              <Link
                key={key}
                href={href}
                className={`concept-boards-workspace-nav__link${active ? " is-active" : ""}`}
                aria-current={active ? "page" : undefined}
              >
                <Icon className="concept-boards-workspace-nav__icon" aria-hidden="true" />
                <span>{label}</span>
              </Link>
            );
          })}
        </div>
      </div>
      <style>{`
        .concept-boards-workspace-nav {
          --cb-nav-ink: #626779;
          --cb-nav-active: #6738c8;
          display: block;
          width: 100%;
          min-width: 0;
          border-bottom: 1px solid #e2e3eb;
        }
        .concept-boards-workspace-nav::-webkit-scrollbar { display: none; }
        .concept-boards-workspace-nav__inner {
          display: flex;
          width: 100%;
          max-width: 1480px;
          box-sizing: border-box;
          min-width: 0;
          align-items: center;
          flex-direction: column;
          margin: 0 auto;
          padding: 0 16px;
        }
        .concept-boards-workspace-nav__identity {
          display: flex;
          min-height: 42px;
          align-self: stretch;
          align-items: center;
          gap: 10px;
          color: #171528;
          font-size: 15px;
          font-weight: 700;
          letter-spacing: -0.035em;
          white-space: nowrap;
        }
        .concept-boards-workspace-nav__mark {
          width: 3px;
          height: 17px;
          margin-left: -16px;
          border-radius: 0 3px 3px 0;
          background: #6738c8;
        }
        .concept-boards-workspace-nav__items {
          display: flex;
          width: 100%;
          min-width: 0;
          overflow-x: auto;
          overscroll-behavior-x: contain;
          scrollbar-width: none;
          -webkit-overflow-scrolling: touch;
          align-items: stretch;
          gap: 3px;
          align-self: flex-start;
        }
        .concept-boards-workspace-nav__items::-webkit-scrollbar { display: none; }
        .concept-boards-workspace-nav__link {
          position: relative;
          display: inline-flex;
          flex-shrink: 0;
          min-height: 46px;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 0 13px;
          color: var(--cb-nav-ink);
          font-family: inherit;
          font-size: 13px;
          font-weight: 500;
          line-height: 1.2;
          letter-spacing: -0.01em;
          text-decoration: none;
          white-space: nowrap;
          transition: color 150ms ease;
          outline: none;
        }
        .concept-boards-workspace-nav__link::after {
          position: absolute;
          right: 7px;
          bottom: 0;
          left: 7px;
          height: 2px;
          background: var(--cb-nav-active);
          content: "";
          opacity: 0;
          transform: scaleX(.6);
          transition: transform 160ms ease, opacity 160ms ease;
        }
        .concept-boards-workspace-nav__link:hover,
        .concept-boards-workspace-nav__link.is-active { color: var(--cb-nav-active); }
        .concept-boards-workspace-nav__link.is-active { font-weight: 600; }
        .concept-boards-workspace-nav__link.is-active::after {
          opacity: 1;
          transform: scaleX(1);
        }
        .concept-boards-workspace-nav__link:focus-visible {
          border-radius: 5px;
          box-shadow: inset 0 0 0 2px #9d83df;
        }
        .concept-boards-workspace-nav__icon { width: 16px; height: 16px; flex: 0 0 auto; stroke-width: 1.8; }
        @media (min-width: 640px) {
          .concept-boards-workspace-nav__inner { padding-right: 28px; padding-left: 28px; }
        }
        @media (min-width: 768px) {
          .concept-boards-workspace-nav__inner {
            min-height: 50px;
            flex-direction: row;
          }
          .concept-boards-workspace-nav__identity {
            min-height: 50px;
            flex: 0 0 auto;
            margin-right: 36px;
            font-size: 15px;
          }
          .concept-boards-workspace-nav__mark { margin-left: 0; }
          .concept-boards-workspace-nav__items { flex: 1; min-width: 0; }
          .concept-boards-workspace-nav__link { min-height: 50px; padding: 0 15px; }
        }
        @media (min-width: 1024px) {
          .concept-boards-workspace-nav__inner { padding-right: 40px; padding-left: 40px; }
        }
        @media (prefers-reduced-motion: reduce) {
          .concept-boards-workspace-nav__link,
          .concept-boards-workspace-nav__link::after { transition: none; }
        }
      `}</style>
    </nav>
  );
}
